import InputCustom from 'components/Inputs'
import InputVariable from 'components/Inputs/InputVariable'
import CustomSelect from 'components/Select'
import useRedux from 'hooks/useRedux'
import React, { useEffect, useState } from 'react'
import { RiMessage3Line } from 'react-icons/ri'
import { useSelector } from 'react-redux'
import {
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap'
import { ApplicationState } from 'store'
import { CADASTRO_MODELO } from 'store/modules/backoffice/cadastroModelo/types'
import { KANBAN } from 'store/modules/kanban/types'
import { CustomButton, SimpleTitleCard } from 'styles/global'
import * as kanbanActions from 'store/modules/kanban/actions'
import * as cadastroModelosActions from 'store/modules/backoffice/cadastroModelo/actions'
import * as yup from 'yup'
import { chatwootSchema } from 'models/schemas'
import { toast } from 'react-toastify'
import { removeMaskPhone } from 'util/masks'

interface Props {
  isOpen: boolean
  onToggle: () => void
  infosCliente: {
    nome: string
    telefone: string
    cpf: string
  }
}

const ModalChatwoot = ({ isOpen, onToggle, infosCliente }: Props) => {
  const { modeloMensagemSelect } = useSelector<
    ApplicationState,
    CADASTRO_MODELO
  >((state: ApplicationState) => state.cadastroModelo)

  const {
    agentesChatwoot,
    inboxesChatwoot,
    agenteChatwootSelected,
    inboxChatwootSelected
  } = useSelector<ApplicationState, KANBAN>((state) => state.kanban)
  const { dispatch } = useRedux()

  const [message, setMessage] = useState<string>('')
  const [modeloSelecionado, setModeloSelecionado] = useState<string>('')
  const [errors, setErrors] = useState<{ invalid: boolean; field: string }[]>(
    []
  )

  const variables = [
    { descricao: 'nome Cliente', campoEquivalente: 'nome_cliente' },
    { descricao: 'nome Operador', campoEquivalente: 'nome_operador' },
    {
      descricao: 'valor da Oportunidade',
      campoEquivalente: 'valor_oportunidade'
    }
  ]

  useEffect(() => {
    if (isOpen) {
      dispatch(kanbanActions.getAgentesChatwootSaga())
      dispatch(kanbanActions.getInboxesChatwootSaga())
      dispatch(kanbanActions.getAgenteSelecionadoChatWootSaga())
      dispatch(cadastroModelosActions.getModelosMensagemSaga())
    } else {
      dispatch(kanbanActions.setAgenteChatwootSelected(null))
      dispatch(kanbanActions.setInboxChatwootSelected(null))
      setModeloSelecionado('')
      setMessage('')
    }
  }, [isOpen, dispatch])

  useEffect(() => {
    if (modeloSelecionado && modeloMensagemSelect?.length) {
      const modelo = modeloMensagemSelect.find(
        (m) => m.id === modeloSelecionado
      )
      if (modelo) {
        setMessage(modelo.descricao)
      }
    }
  }, [modeloSelecionado, modeloMensagemSelect])

  const enviarMensagem = () => {
    setErrors([])
    chatwootSchema
      .validate(
        {
          agenteChatwootSelected,
          inboxChatwootSelected,
          destinatario: infosCliente.nome,
          message,
          modeloSelecionado
        },
        { abortEarly: false }
      )
      .then(() => {
        dispatch(
          kanbanActions.enviarMensagemChatwootSaga({
            mensagem: message,
            agentId: agenteChatwootSelected || '',
            inboxId: inboxChatwootSelected || '',
            cpfCliente: infosCliente.cpf,
            numeroTelefone: removeMaskPhone(infosCliente.telefone)
          })
        )
        dispatch(kanbanActions.cleanModalChatwoot())
        setMessage('')
        onToggle()
      })
      .catch((err) => {
        console.error('Erro de validação:', err.inner)
        const newErrors = err.inner.map((error: yup.ValidationError) => ({
          invalid: true,
          field: error.path
        }))
        setErrors(newErrors)
        toast.error('Por favor, corrija os erros no formulário.')
      })
  }

  return (
    <Modal
      size="md"
      isOpen={isOpen}
      toggle={() => onToggle()}
      backdrop="static"
    >
      <ModalHeader toggle={onToggle} className="d-flex align-items-center">
        <SimpleTitleCard>
          <RiMessage3Line className="icon me-2" />
          Preencha as informações para enviar mensagem:
        </SimpleTitleCard>
      </ModalHeader>

      <ModalBody>
        <FormGroup>
          <Label className="label-12">Agente*</Label>
          <CustomSelect
            options={agentesChatwoot}
            placeholder="Selecione o Agente"
            value={agenteChatwootSelected}
            onChange={(id) =>
              dispatch(kanbanActions.setAgenteChatwootSelected(id))
            }
            accessorLabel="name"
            accessorValue="id"
            isClearable
            invalid={errors.some(
              (error) => error.field === 'agenteChatwootSelected'
            )}
          />
        </FormGroup>

        <FormGroup>
          <Label className="label-12">Conexão*</Label>
          <CustomSelect
            options={inboxesChatwoot}
            placeholder={'Selecione a Conexão'}
            value={inboxChatwootSelected}
            onChange={(id) => {
              dispatch(kanbanActions.setInboxChatwootSelected(id))
            }}
            accessorLabel="name"
            accessorValue="id"
            isClearable
            invalid={errors.some(
              (error) => error.field === 'inboxChatwootSelected'
            )}
          />
        </FormGroup>

        <FormGroup>
          <Label className="label-12">Para</Label>
          <InputCustom type="text" value={infosCliente.nome} disabled />
        </FormGroup>

        <FormGroup>
          <Label className="label-12">Mensagem*</Label>
          <InputVariable
            rawValue={message}
            onRawChange={(value) => {
              const trimmedValue = value.slice(0, 300)
              setMessage(trimmedValue)
            }}
            variables={variables}
            placeholder="Escreva sua mensagem aqui..."
            invalid={errors.some((error) => error.field === 'message')}
          />
          <small>{message.length}/300 caracteres</small>
        </FormGroup>

        <FormGroup>
          <Label className="label-12">Selecione o Modelo</Label>
          <CustomSelect
            options={
              Array.isArray(modeloMensagemSelect) ? modeloMensagemSelect : []
            }
            placeholder="Escolha um modelo"
            value={modeloSelecionado}
            onChange={(id) => {
              setModeloSelecionado(id)
              if (!id) setMessage('')
            }}
            accessorLabel="nome"
            accessorValue="id"
            isClearable
          />
        </FormGroup>
      </ModalBody>

      <ModalFooter>
        <Row className="d-flex column-gap-20 w-100 justify-content-center">
          <CustomButton background="#497a4bff" onClick={enviarMensagem}>
            Enviar
          </CustomButton>
          <CustomButton
            onClick={() => {
              onToggle()
              dispatch(kanbanActions.cleanModalChatwoot())
              setMessage('')
            }}
            background="#E53935"
          >
            Cancelar
          </CustomButton>
        </Row>
      </ModalFooter>
    </Modal>
  )
}

export default ModalChatwoot
